import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import { useActiveTab } from 'components/WorkspaceEditor/Workspace2Store'
import { useEffect, useState } from 'react'

// Constants for magic numbers
const PRODUCT_TYPE_ACTIVE_TAB = 2
const RETRY_DELAY_MS = 100

/**
 * This hook is used to get the height of a container element
 * based on a CSS selector. It uses the ResizeObserver API to
 * observe changes in the height of the container
 * and updates the height state accordingly.
 * It initializes the height with a default value
 * and returns the current height of the container.
 * @param selector
 * @param defaultHeight
 * @returns number
 */
export const useContainerHeight = (selector: string, defaultHeight: number) => {
    const [height, setHeight] = useState<number>(defaultHeight)
    const elementType = useElementItemShallowSelector(
        (state) => state.elementItemValue.elementType
    )
    const activeTab = useActiveTab()

    useEffect(() => {
        const shouldNotSetHeight =
            elementType === ProjectElementType.PRODUCTTYPE && activeTab !== PRODUCT_TYPE_ACTIVE_TAB
        if (shouldNotSetHeight) return

        const findElement = () => document.querySelector(selector) as HTMLElement | null
        const htmlElement = findElement()
        
        if (!htmlElement) {
            // Retry once after a short delay for elements that may not be immediately available
            const retryTimeout = setTimeout(() => {
                const retryElement = findElement()
                if (retryElement) {
                    const updateHeight = () => setHeight(retryElement.offsetHeight)
                    const observer = new window.ResizeObserver(updateHeight)
                    observer.observe(retryElement)
                    updateHeight() // Set initial height
                }
            }, RETRY_DELAY_MS)
            return () => clearTimeout(retryTimeout)
        }

        const updateHeight = () => setHeight(htmlElement.offsetHeight)

        const observer = new window.ResizeObserver(updateHeight)
        observer.observe(htmlElement)
        updateHeight() // Set initial height

        return () => observer.disconnect()
    }, [selector, elementType, activeTab])

    return height
}
